'use client';

import { useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';

import { Button } from '@/shared/ui/shadcn/button';
import { Loader2, RotateCcw } from 'lucide-react';
import { useRenderStore } from '../../model/render-form.store';
import { useJobQueueStore } from '@/features/design/job-queue';
import { isRenderInQueue } from '@/shared/lib/render-status';
import { useRenderPreflight } from '../../hooks/use-render-preflight';
import { useTokenNudge } from '@/shared/api/hooks/use-user';
import { apiClient } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';
import { isEmptyRefine } from '../../lib/render-settings-storage';
import { useSectionChanges } from '../../model/render-form-store.selectors';
import {
  trackEvent,
  AnalyticsEvent,
  BlockReason,
} from '@/shared/lib/analytics';
import {
  ScrollableRenderSettings,
  SettingsDisplayProvider,
  type ScrollableRenderSettingsRef,
} from '../scrollable-settings';
import { ErrorBoundary } from '@/shared/ui';
import { useNestedSheetStack } from '@/shared/ui/NestedSheet';
import { cn } from '@/shared/lib/cn';
import { FOOTER_REVEAL_TRANSFORM } from '@/shared/lib/bottom-bar-reveal';
import type { Control, Angle, Concept } from '@/types/concept';
import type { RenderCreateRequest } from '@/types/render';

interface RenderPanelContentProps {
  // Data
  projectId: string;
  concept: Concept;
  conceptAngleId?: string;
  angle: Angle | null;
  /** Server-localized side a CONSTRUCTED view looks at; null for uploads. */
  viewpointLabel?: string | null;
  controls: Control[];
  defaultValues: Partial<RenderCreateRequest['data']['attributes']>;
  variant: 'main' | 'derivative';

  // State
  isCreating: boolean;
  error: string | null;
  hasAnyChanges: boolean;

  // Actions
  onSubmit: (data: RenderCreateRequest) => Promise<void>;
  onResetChanges: () => void;
  onRefetch?: () => void;
  renderSettingsRef: React.RefObject<ScrollableRenderSettingsRef | null>;

  // UI options
  hideIcons?: boolean;
  /** Explore main-angle with empty project goals — block Generate until the
   *  user fills the brief (mirrors the goals red dot + brief gate). Computed
   *  by UnifiedRenderPanel so the drawer header and inline footer agree. */
  goalsRequired?: boolean;
  /** A refine render is in flight for this angle → the settings are a read-only
   *  readout of what's rendering (locked, dimmed, Refine pill shown). */
  isReadOnly?: boolean;
  /** Parent render (image being refined) image + id — point the toggle at it so
   *  the Refine pill shows while read-only. */
  readOnlyParentRenderUrl?: string | null;
  readOnlyParentRenderId?: string | null;
  /** Stacked (single-column) layout: the settings render in normal page flow
   *  (no internal scroll) and the Generate CTA floats `fixed` above the bottom
   *  nav as a centered capsule (Apple/Android parity), instead of overlaying
   *  the bottom of a fixed-height panel. */
  isStacked?: boolean;
  /** Tailwind `bottom-*` class for the fixed Generate capsule in stacked mode
   *  — clears the floating bottom-nav island when it's visible. */
  footerBottomClassName?: string;
}

export function RenderPanelContent({
  projectId,
  concept,
  conceptAngleId,
  angle,
  viewpointLabel,
  controls,
  defaultValues,
  variant,
  isCreating,
  error,
  hasAnyChanges,
  onSubmit,
  onResetChanges,
  onRefetch,
  renderSettingsRef,
  hideIcons = false,
  goalsRequired = false,
  isReadOnly = false,
  readOnlyParentRenderUrl = null,
  readOnlyParentRenderId = null,
  isStacked = false,
  footerBottomClassName = 'bottom-5',
}: RenderPanelContentProps) {
  const tButton = useTranslations('render.button');
  const tCommon = useTranslations('common');

  const formData = useRenderStore((state) => state.formData);
  const currentMode = formData?.mode ?? 'explore';

  // True once `initializeFormData` has resolved THIS panel's context (concept
  // + angle) — before that, `formData` may still hold the previous context's
  // mode/draft. Used to defer the sheet-opened funnel event below.
  const isInitializedForContext = useRenderStore(
    (state) =>
      state.lastInitializedKey !== null &&
      state.currentContext?.conceptId === concept.id &&
      state.currentContext?.angleId === conceptAngleId
  );

  /**
   * Generate-button label depends ONLY on the draft mode, not on which angle
   * is selected (cross-platform parity with Apple/Android). Every Generate
   * produces a design variant of the current angle, so the wording is identical
   * on the main angle and on derivative angles:
   *   mode = explore → "Create new variant"
   *   mode = refine  → "Create refined variant"
   */
  const generateButtonLabel =
    currentMode === 'refine'
      ? tButton('createRefinedRender')
      : tButton('createNewRender');
  // Refine without any user input would render an identical copy — block
  // Generate (cross-platform hard gate, see Apple/Android parity).
  //
  // Exception: changing the render style (output preset OR custom file
  // reference) so it differs from the parent render IS a meaningful refine —
  // the user wants the same design re-rendered in another style. The render
  // style is excluded from `isEmptyRefine` (it's a SHARED technical knob, so a
  // value merely carried over / restored must not unblock a blank refine), but
  // the Output change marker diffs against the frozen baseline, so it is true
  // ONLY on a genuine in-session change — exactly the signal we want here.
  // Cross-platform parity with Apple/Android.
  const sectionChanges = useSectionChanges();
  const outputChangedInRefine =
    currentMode === 'refine' &&
    'output' in sectionChanges &&
    sectionChanges.output === true;
  const emptyRefine = isEmptyRefine(formData) && !outputChangedInRefine;

  // Snapshot whether Generate is actionable when the panel opens. In refine
  // mode the button opens disabled (empty-refine gate) — the silent drop-off
  // we measure against generate_tapped. Fires exactly ONCE per angle
  // (conceptAngleId) and panel variant — not on every keystroke — but only
  // AFTER `initializeFormData` resolved this context: firing earlier read a
  // stale mode/emptyRefine from the previous context's form data. Event and
  // param names are a cross-platform funnel contract — do not rename.
  const sheetOpenedFiredKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isInitializedForContext) return;
    const key = `${conceptAngleId ?? 'main'}:${variant}`;
    if (sheetOpenedFiredKeyRef.current === key) return;
    sheetOpenedFiredKeyRef.current = key;
    const params: Record<string, string | number | boolean> = {
      mode: currentMode,
      angle_kind: variant,
      generate_enabled: !emptyRefine,
    };
    if (emptyRefine) params.disabled_reason = BlockReason.EMPTY_REFINE;
    trackEvent(AnalyticsEvent.RENDER_SHEET_OPENED, params);
  }, [
    isInitializedForContext,
    conceptAngleId,
    variant,
    currentMode,
    emptyRefine,
  ]);

  // Server-driven "save as reference" steer nudge: ack on display so each
  // "Design #N" milestone fires at most once. The banner only renders on the
  // main-angle panel, so ack from there too — fire-and-forget, idempotent
  // server-side (the latch stores the highest milestone shown).
  const referenceNudge = concept.meta?.reference_nudge ?? null;
  useEffect(() => {
    if (variant === 'main' && referenceNudge) {
      void apiClient.acknowledgeReferenceNudge(concept.id);
    }
  }, [variant, concept.id, referenceNudge]);

  // Apple-parity plan gate (subscription → tokens → queue). Each branch shows
  // the server-driven paywall and aborts the submit before the render fires.
  const runPreflight = useRenderPreflight();

  // Server-driven low-balance nudge. Non-blocking: when the wallet is in the
  // "running low" band the server returns `user.tokens.nudge`; we surface it
  // as an info toast right after the render is submitted. The render always
  // proceeds — this is a soft heads-up, never a gate (the hard
  // INSUFFICIENT_TOKENS paywall is handled by preflight above).
  const nudge = useTokenNudge();

  // Collapse any open sub-panel (model / style / structure …) on Generate — see
  // handleGenerateClick.
  const { closeAllNestedSheets, hasOpenNestedSheet, setPortalContainer } =
    useNestedSheetStack();

  const handleGenerateClick = () => {
    if (runPreflight()) return;
    if (renderSettingsRef.current?.submitRender) {
      renderSettingsRef.current.submitRender();
      // Land the user back on the concept view so they SEE the render start.
      // Generating from deep inside a sub-panel otherwise leaves it covering the
      // progress; the button re-arms instantly and users re-tap → duplicate
      // renders. Collapse all sub-panels and, on the single-column page where
      // everything shares one scroll, snap back to the top (the main image with
      // its progress sits there). Two-column keeps the image in its own column,
      // always visible, so no scroll is needed there.
      closeAllNestedSheets();
      if (isStacked && typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      if (nudge) {
        // Server-localized copy, rendered verbatim. Action routes to the
        // tokens top-up surface; render is already on its way regardless.
        // `primary_action` may be null (info-only payload) — then the toast
        // carries no action button.
        toast.info(nudge.title, {
          description: nudge.body,
          action: nudge.primary_action
            ? {
                label: nudge.primary_action.label,
                onClick: () => {
                  window.location.href = '/account/tokens';
                },
              }
            : undefined,
        });
        // Confirm display so the server arms its once-per-band dedupe. The
        // nudge is non-null only while in the low-balance band and the toast
        // fires exactly once per submit, so this acks once per actual show.
        // Fire-and-forget — swallowed inside the client, never blocks render.
        void apiClient.acknowledgeTokenNudge();
      }
    }
  };

  // True while a render for the CURRENT angle is active in the job queue
  // (cross-device truth: the optimistic pending job lands right after the
  // POST, then WebSocket snapshots take over). Drives the busy Generate
  // button + the parallel "+1" affordance. Upscales deliberately excluded.
  // Matches Apple's `isAngleRendering` / Android's `isAngleRendering`.
  const isAngleRendering = useJobQueueStore((state) =>
    state.jobQueue.some(
      (j) =>
        !!conceptAngleId &&
        j.conceptAngleId === conceptAngleId &&
        j.jobType === 'render' &&
        isRenderInQueue(j.status)
    )
  );

  // The render in flight for this angle is a refine — parallel "+1" is
  // explore-only, so suppress it while a refine renders (the panel mode flips to
  // explore during a pending render, so currentMode alone can't distinguish the
  // two; the active render's server-reported `mode` is the source of truth).
  // Parity with Apple / Android.
  const isActiveRenderRefine = useJobQueueStore((state) =>
    state.jobQueue.some(
      (j) =>
        !!conceptAngleId &&
        j.conceptAngleId === conceptAngleId &&
        j.jobType === 'render' &&
        isRenderInQueue(j.status) &&
        j.mode === 'refine'
    )
  );

  // At least one free parallel-render slot per the server-authoritative queue
  // aggregate. The "+1" affordance is suppressed once the building is at its
  // concurrency cap (e.g. 2 active renders on a Facade Kit building) — that's
  // the theoretical max, so there is nothing to queue and we must not dangle a
  // button that only leads to the queue-full dialog. Null aggregate (cold
  // start) → allow; the per-click preflight still gates. Parity with Apple's
  // `hasFreeQueueSlot` / Android's `hasFreeQueueSlot`.
  const hasFreeQueueSlot = useJobQueueStore(
    (state) => (state.queueAggregate?.availableSlots ?? 1) > 0
  );

  // Generate disabled gate — shared by every entry point (header, panel
  // footer, floating capsule): in-flight or angle-busy (parallel starts go
  // through "+1"), empty-refine hard gate, or Explore-main with empty
  // project goals.
  const generateDisabled =
    isCreating || isAngleRendering || emptyRefine || goalsRequired;
  // "+1" keeps every gate except the busy state itself — `isCreating` still
  // guards the in-flight POST window against double-clicks.
  const queueOneMoreDisabled = isCreating || emptyRefine || goalsRequired;

  // The reset + Generate pair. `floating` shapes the Generate as a centered
  // pill (stacked single-column, above the nav island); `panel` fills the
  // inspector column width (two-column inline panel footer).
  const footerButtons = (mode: 'panel' | 'floating') => (
    <>
      {/* Reset is shown only with changes AND nothing rendering for this angle:
          while a render is in flight the form is a read-only readout (refine) or
          a busy explore, so resetting it is meaningless — hide it rather than
          dangle a disabled control next to the busy Generate. Parity with
          Apple/Android. */}
      {hasAnyChanges && !isCreating && !isAngleRendering && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onResetChanges}
          title={tCommon('resetChanges')}
          /* size-8 (32px) keeps Reset a touch under the Generate button so it
             reads as a quiet secondary action, not a peer CTA — cross-platform
             parity with Apple/Android. `size="icon"` alone matched Generate's
             height in the panel footer. */
          className="size-8 pointer-events-auto bg-card shadow-md rounded-full"
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
      )}
      <Button
        variant="default"
        size="cta"
        onClick={handleGenerateClick}
        disabled={generateDisabled}
        // Shared CTA capsule (size="cta") — content-hugging, centered by the
        // footer below; only the contextual shadow differs (soft inline vs
        // raised floating).
        className={cn(
          'pointer-events-auto',
          mode === 'panel' ? 'shadow-md' : 'shadow-lg shadow-black/20'
        )}
      >
        {/* Spinner sits BEFORE the title (HIG busy-button pattern, Apple
            parity); the label keeps the regular Generate copy while busy. */}
        {(isCreating || isAngleRendering) && (
          <Loader2 className="h-4 w-4 animate-spin" />
        )}
        {generateButtonLabel}
      </Button>
      {/* Parallel-render "+1" pill, shown next to the busy Generate button
          while a render for the current angle is active. Functionally
          identical to Generate — it exists to make the already-supported
          parallel rendering discoverable.
          EXPLORE-ONLY: parallel variants only make sense while forming a new
          design. In Refine the user asks for one specific fix — fanning out
          multiple refines just burns render budget, so the pill is suppressed
          (parity with Apple/Android). Hidden at the concurrency cap (no free
          slot) — the theoretical max is reached, nothing more to queue. */}
      {isAngleRendering &&
        currentMode === 'explore' &&
        hasFreeQueueSlot &&
        !isActiveRenderRefine && (
        <Button
          variant="default"
          size="cta"
          onClick={handleGenerateClick}
          disabled={queueOneMoreDisabled}
          title={tButton('queueOneMore')}
          aria-label={tButton('queueOneMore')}
          className={cn(
            'pointer-events-auto shadow-md',
            mode === 'floating' && 'shadow-lg shadow-black/20'
          )}
        >
          +1
        </Button>
      )}
    </>
  );

  const settingsList = (
    /* The empty-refine attention dot now lives inside the design-settings card
       itself (see ScrollableRenderSettings → dockedToggleCard) so it anchors to
       the group's top-right corner instead of floating beside it. */
    <div className="h-full">
      <ErrorBoundary
        fallback={
          <div className="text-center py-8">
            <p className="text-destructive mb-4">
              Failed to load render settings
            </p>
            <Button onClick={() => window.location.reload()}>
              Reload Page
            </Button>
          </div>
        }
      >
        {angle ? (
          <ScrollableRenderSettings
            ref={renderSettingsRef}
            onSubmit={onSubmit}
            projectId={projectId}
            conceptId={concept.id}
            conceptAngleId={conceptAngleId}
            variant={variant}
            defaultValues={defaultValues}
            controls={controls}
            angle={angle}
            viewpointLabel={viewpointLabel}
            variantNudge={concept.meta?.variant_nudge ?? null}
            referenceNudge={referenceNudge}
            isReadOnly={isReadOnly}
            readOnlyParentRenderUrl={readOnlyParentRenderUrl}
            readOnlyParentRenderId={readOnlyParentRenderId}
            onControlsUpdate={(_updatedControls) => {
              onRefetch?.();
            }}
            /* Reserve scroll-bottom room when the floating CTA overlays the
               scroll area (two-column panel). Stacked clears the fixed capsule
               via the page wrapper's bottom padding, so no internal pad. */
            bottomScrollPadding={!isStacked ? 'lg' : 'sm'}
            stacked={isStacked}
          />
        ) : (
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">{tCommon('noAngleData')}</p>
          </div>
        )}
      </ErrorBoundary>
    </div>
  );

  const content = (
    <>
      {/* Error display */}
      {error && (
        <div className="bg-destructive/10 text-destructive px-4 py-2 text-form-help">
          {error}
        </div>
      )}

      {isStacked ? (
        /* Stacked (single-column): settings flow in the page scroll; the
           Generate capsule floats `fixed` above the bottom nav island
           (shaped like it), always visible over the scroll (Apple parity). */
        <>
          {settingsList}
          {/* Hidden while a sub-panel is open: the full-screen drill-down owns
              the whole viewport (Back + content only), so the floating Generate
              capsule must not hang over it. Returns on the way back to MAIN.
              Also hidden when the goals gate is active: the floating capsule
              overlays the scroll, so rather than leave a dead disabled pill over
              the photo we hide it entirely — the in-list Project goals row is the
              sole CTA. The two-column inline footer keeps the disabled button
              (parity with Apple/Android wide inspector). */}
          <div
            className={cn(
              'fixed inset-x-0 z-40 flex justify-center px-4 pointer-events-none',
              footerBottomClassName,
              (hasOpenNestedSheet || goalsRequired) && 'hidden'
            )}
            // Ride the scroll-linked nav reveal: drop toward the screen edge
            // as the nav hides (page top), lift back above it as it reveals.
            style={{
              transform: FOOTER_REVEAL_TRANSFORM,
              // Center over the content column, not the viewport. The leading
              // inset clears the sidebar when expanded; the trailing inset mirrors
              // the collapsed symmetric gutter so the capsule stays centered
              // over the (also-centered) content rather than being shoved right
              // by the leading-only offset (see DesktopShell `--content-inset-*`).
              paddingInlineStart: 'calc(1rem + var(--content-inset-start, 0px))',
              paddingInlineEnd: 'calc(1rem + var(--content-inset-end, 0px))',
            }}
          >
            <div className="flex items-center gap-2">
              {footerButtons('floating')}
            </div>
          </div>
        </>
      ) : (
        /* Two-column inline panel: ScrollableRenderSettings owns the scroll
            internally (its ScrollArea), with the Explore/Refine toggle PINNED
            above that scroll. The wrapper here must NOT add its own
            `overflow-y-auto` — a second scroll container around the pinned
            structure makes the whole thing (toggle included) scroll as one, so
            the toggle no longer stays fixed. It just gives the panel a definite
            height (`absolute inset-0`) for the inner `h-full` chain. The bottom
            CTA overlays the scroll area (`pointer-events-none` wrapper so taps on
            the empty strip pass through; only the buttons capture clicks). */
        <div ref={setPortalContainer} className="relative flex-1 min-h-0">
          {/* `ref={setPortalContainer}` registers THIS column as the drill-down
              anchor: every sub-panel (NestedSheet) portals in here and fills the
              full column — covering the Generate footer — instead of clipping to
              whatever `relative` ancestor it happens to sit under (the docked
              Explore/Refine toggle card trapped first-group panels otherwise). */}
          {/* Fade the last stretch of the list toward the bottom edge so the
              floating Generate reads against emptiness instead of against rows
              sliding under it. The mask covers the LIST only — the footer is a
              sibling below, so it keeps full contrast. Mirrors Apple
              `RenderSettingsPanel.bottomFadeMask` and Android. */}
          <div
            className="absolute inset-0"
            style={{
              maskImage:
                'linear-gradient(to bottom, black 0, black 88%, transparent 100%)',
              WebkitMaskImage:
                'linear-gradient(to bottom, black 0, black 88%, transparent 100%)',
            }}
          >
            {settingsList}
          </div>
          {/* The sub-panel (NestedSheet) covers the column but the footer is a
              later sibling that would paint over it — hide it while open so the
              drill-down reads as the whole column (Back + content only). */}
          {!hasOpenNestedSheet && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 px-4 py-3 flex items-center justify-center gap-2">
              {footerButtons('panel')}
            </div>
          )}
        </div>
      )}
    </>
  );

  if (hideIcons) {
    return (
      <SettingsDisplayProvider hideIcons>{content}</SettingsDisplayProvider>
    );
  }

  return content;
}
